import { useEffect, useRef, useState } from 'react';
import { sincronizarVendasPendentes, onSyncStatusChange } from '@/lib/syncManager';
import { offlineDb } from '@/lib/offlineDb';

export type StatusConexao = 'online' | 'offline' | 'sincronizando';

export interface OnlineStatus {
  online: boolean;
  statusConexao: StatusConexao;
  vendasPendentes: number;
  conflitos: number;
}

export function useOnlineStatus(): OnlineStatus {
  const [online,          setOnline]          = useState(navigator.onLine);
  const [statusConexao,   setStatusConexao]   = useState<StatusConexao>(navigator.onLine ? 'online' : 'offline');
  const [vendasPendentes, setVendasPendentes] = useState(0);
  const [conflitos,       setConflitos]       = useState(0);

  const sincronizandoRef = useRef(false);

  useEffect(() => {
    // Carrega contadores iniciais
    offlineDb.contarPendentes().then(setVendasPendentes);
    offlineDb.contarConflitos().then(setConflitos);

    // Sync inicial: recupera vendas órfãs em 'Sincronizando' de uma sessão
    // interrompida (refresh/fechamento com o POST em voo) e reenvia pendentes
    if (navigator.onLine) {
      sincronizarVendasPendentes().catch(() => undefined);
    }

    const atualizar = (pendentes: number, conf: number) => {
      setVendasPendentes(pendentes);
      setConflitos(conf);
      if (pendentes > 0 && navigator.onLine) {
        setStatusConexao('sincronizando');
        sincronizandoRef.current = true;
      } else if (navigator.onLine) {
        setStatusConexao('online');
        sincronizandoRef.current = false;
      }
    };

    const unsub = onSyncStatusChange(atualizar);

    const handleOnline = () => {
      setOnline(true);
      setStatusConexao('sincronizando');
      sincronizarVendasPendentes().then(() => {
        setStatusConexao('online');
      });
    };

    const handleOffline = () => {
      setOnline(false);
      setStatusConexao('offline');
    };

    window.addEventListener('online',  handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      unsub();
      window.removeEventListener('online',  handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  return { online, statusConexao, vendasPendentes, conflitos };
}
